import { LitElement, html, css, nothing } from 'lit';
import { resolveHref, isWwwFamily, localeSwitchPath } from './resolve-href.js';
import { footerColumns, footerSocial, footerBottom, footerLanguages } from './data/footer-data.js';
import { buildFooterJsonLd } from './footer-jsonld.js';
import { getCopy, resolveCopy, resolveItemCopy } from './i18n/copy.js';

// <postman-footer> — RUNTIME (client-mounted) component.
//
// Ported from the marketing site's Footer.jsx: five link columns, a social row,
// and a bottom bar (download + privacy policy + copyright). Because it mounts on
// the client, it reserves height (--pm-footer-min-height) to avoid layout shift,
// and it emits the footer JSON-LD into LIGHT DOM (not the shadow root) so it is
// discoverable as structured data.
//
// `emit-jsonld` (default on) controls that light-DOM JSON-LD emit. Consumers
// that render the SiteNavigationElement structured data themselves at build
// time — e.g. www-next, which emits a static block into its static-export HTML
// so it's in the pre-JS markup — set `emit-jsonld="false"` to avoid a duplicate
// (see docs/PLAN.md decision #8). Build the same JSON with the exported
// buildFooterJsonLd(footerColumns).
//
// Host-specific pieces go in slots:
//   - `privacy`   the host's consent-manager controls (Transcend, "Do Not Sell")
//   - `languages` the host's own language switcher, if it has one
//
// LANGUAGE SWITCHER. www renders one; most other consumers should not. A
// property is not enough to decide that for them — a docs site or a support
// centre that serves only English would be offering a translation that does not
// exist — so it is OPT-IN per consumer, three ways, most specific first:
//
//   1. slot content in `languages`  -> the host's own control, used verbatim
//   2. `language-switcher` attr     -> the built-in control (what www wants)
//   3. neither                      -> no switcher, no reserved space
//
// Opt-in rather than opt-out because this ships to www, blog, Fern and Zendesk
// off one CDN path: defaulting it on would silently add a JA link to properties
// that have no JA pages, and the first anyone would hear of it is a 404.
export class PostmanFooter extends LitElement {
  static properties = {
    host: { type: String },
    locale: { type: String },
    year: { type: Number },
    // String (not Boolean): default-on with an explicit `emit-jsonld="false"`
    // opt-out. A Boolean attribute keys off presence, so `="false"` would still
    // read as true — the opposite of what a suppress flag needs.
    emitJsonld: { attribute: 'emit-jsonld' },
    // Full translation opt-out — English copy and no locale prefixing. Same
    // string-not-boolean reasoning as emit-jsonld above. Mirrors
    // <postman-navbar> so a host does not have to opt out differently per
    // element.
    translate: { type: String },
    // Boolean IS right here: the flag is default-OFF, so presence-means-true is
    // exactly the semantics, and `<postman-footer language-switcher>` reads the
    // way an HTML author expects.
    languageSwitcher: { type: Boolean, attribute: 'language-switcher' },
    // `theme="dark"` mirrors <postman-navbar>'s attribute — see its doc comment
    // for why this is consumer-declared rather than derived from the pathname.
    // On www-next both elements get the same attribute from the same
    // `isDarkHomepage()` check, so the navbar and footer never disagree.
    //
    // `reflect: true` for the same reason as <postman-navbar>'s `theme`: the
    // dark styling below is `:host([theme='dark'])` attribute selectors,
    // which a non-reflecting property would never activate.
    theme: { type: String, reflect: true },
    _langOpen: { state: true },
    _hasSlottedLanguages: { state: true },
  };

  static styles = css`
    /* Design tokens match www.postman.com's Footer (Footer.module.css): white
       background, grey-50 (#707070) text, Inter, 1224px max width, 60px pad.
     *
     * SPACING VALUES ARE MEASURED FROM PRODUCTION, NOT CONVERTED FROM THE CLASS
     * NAMES. www-next's Tailwind spacing scale is NOT the stock one: its
     * tailwind.config.js maps step N to N rem, and global.css sets the root
     * font-size to 62.5% (10px). So gap-2 there is 20px, not the stock 8px —
     * every step is 10px. Reading a my-2 / gap-4 off Footer.jsx and writing the
     * stock 8px/16px here is how v1.0.4 ended up 2.5x too tight around the
     * horizontal rule (and everywhere else). Literal px in Footer.module.css —
     * h2's 8px margin, .link's 5px-0 padding — carries over as-is.
     *
     * Verify against production with getComputedStyle, not by eye. */
    :host {
      display: block;
      min-height: var(--pm-footer-min-height, 320px);
      background: var(--pm-footer-bg, #fff);
      color: var(--pm-footer-fg, #707070);
      font-family: var(--pm-font, 'Inter', system-ui, -apple-system, sans-serif);
      border-top: 1px solid var(--pm-footer-rule, #e6e6e6);
      /* global.css puts letter-spacing:-0.16px on "body, p", so every string in
         www's footer inherits it — the column links, the copyright line and the
         language options all measure tighter than they do here at "normal".
         Inherited rather than set per rule, matching how the site gets it; the
         headings override it below, as h2 does there. */
      letter-spacing: var(--pm-letter-spacing, -0.16px);
    }
    /* border-box because 1224px is www's BORDER-box width: its container is
       max-w-[1224px] with px-2, under a global box-sizing:border-box reset, so
       the padding eats into the 1224 rather than adding to it. That page reset
       does not cross into the shadow root, so it has to be set here or the
       footer ends up 40px wider than the rest of the page. 20px = px-2. */
    .inner {
      box-sizing: border-box;
      max-width: 1224px;
      margin: 0 auto;
      padding: 60px 20px;
    }
    /* Footer.module.css drops the block padding to 30px at this width. */
    @media (max-width: 576px) {
      .inner {
        padding: 30px 20px;
      }
    }
    /* 40px = www's lg:gap-4, and only at lg: below it www drops the column gap
       to 0. An earlier revision kept 40px at every width "rather than letting
       the link columns touch" — but with the gap gone each cell is 181px wide
       against 161px, and the longest label in the footer is ~140px, so nothing
       touches; keeping it instead narrowed every mobile column, wrapped labels
       the site fits on one line, and pushed each row lower than the site's. */
    .columns {
      display: grid;
      /* SIX EQUAL columns, brand cell included: www's grid is grid-cols-12 with
         a col-span-2 per cell, so its brand column is as wide as a link column
         (164px at 1440), not the 100px of the logo inside it. Sizing it to the
         logo pulled every link column 64px left of the site's. */
      grid-template-columns: repeat(6, 1fr);
      gap: 40px;
    }
    /* 1023.98px mirrors www's lg: breakpoint (min-width: 1024px) from
       tailwind.config.js screens — NOT Bootstrap's 992px, which is what v1.0.4
       used and which flipped the whole footer to its stacked layout 32px early. */
    @media (max-width: 1023.98px) {
      .columns {
        grid-template-columns: repeat(3, 1fr);
        column-gap: 0;
        /* The rows still need separating; www gets ~20px from its own nav
           spacing. */
        row-gap: 20px;
        /* mb-3 (30px), paired with lg:mb-0 on www. This is the entire
           columns-to-social gap below lg; at desktop that gap is 0. */
        margin-bottom: 30px;
      }
      .logo {
        grid-column: 1 / -1;
        /* www's order-last / lg:order-first: below lg the brand block follows the
           link columns instead of opening the footer. */
        order: 1;
      }
    }
    @media (max-width: 576px) {
      .columns {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    /* The brand link is not a text link: without this it inherits the column
       links' 5px block padding and drops the mark 5px below the headings. */
    .logo a {
      padding: 0;
    }
    .logo img {
      width: 100px;
      height: 90px;
      object-fit: contain;
    }
    /* mx-auto below sm, mx-0 above it — on the ANCHOR, which is what has width
       here; centring the img inside an inline-block anchor does nothing. */
    @media (max-width: 576px) {
      .logo {
        text-align: center;
      }
    }
    /* +0.48px, POSITIVE, against the -0.16px everything around it inherits.
       Footer.module.css's h2.title overrides the site's h2 font-size, family,
       line-height and colour but NOT its letter-spacing, so these headings keep
       the tracking global.css sets for a 28px display h2 while rendering at
       16px. Measured off production, not intended — but it is what ships, and
       at "normal" the headings sat ~1px narrower per character than the site's. */
    h2 {
      font-size: 16px;
      font-weight: 600;
      line-height: 1.4;
      letter-spacing: var(--pm-footer-heading-letter-spacing, 0.48px);
      margin: 0 0 8px;
      color: var(--pm-footer-heading, #707070);
    }
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    li {
      font-size: 14px;
    }
    a {
      color: var(--pm-footer-link, #707070);
      text-decoration: none;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.7;
      padding: 5px 0;
      display: inline-block;
    }
    /* Footer.module.css's a.link is display:flex, so a column link fills its
       164px cell and the whole row is clickable; inline-block shrank the target
       to the label (121px for "Postman Overview"). Scoped to the column links —
       the bottom-bar actions and the social row have their own rules, and the
       social row's padding was measured against an inline box. */
    li > a {
      display: flex;
    }
    a:hover {
      color: var(--pm-footer-link-hover, #0056b3);
    }
    :focus-visible {
      outline: 2px solid var(--pm-focus-ring, #ff6c37);
      outline-offset: 2px;
    }
    /* LANGUAGE SWITCHER — values read off www's rendered markup, not guessed.
       The block is mt-4 (40px on that 10px-root scale) under the last column's
       nav, so it lines up with the Company heading rather than floating in the
       gutter. */
    /* www's cell is "flex flex-col justify-between", so the switcher sits at the
       BOTTOM of the column — the row is as tall as the longest one (API Network,
       12 links), which is 65px below where stacking alone would put it. */
    .col-langs {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    .languages {
      margin-top: 40px;
    }
    /* Matches www's trigger: w-full so the chevron sits at the column's right
       edge, py-[5px] to share the .link rhythm, and grey-50 rather than the
       action blue — it is a disclosure, not a destination. */
    .lang-trigger {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      appearance: none;
      -webkit-appearance: none;
      border: 0;
      background: transparent;
      padding: 5px 0;
      font: inherit;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.7;
      color: var(--pm-footer-link, #707070);
      cursor: pointer;
    }
    .lang-trigger:hover {
      color: var(--pm-footer-link-hover, #0056b3);
    }
    .lang-current {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .lang-chevron {
      transition: transform 0.2s ease-in-out;
      transform: scale(0.85);
      flex: none;
    }
    .lang-trigger[aria-expanded='true'] .lang-chevron {
      transform: scale(0.85) rotate(180deg);
    }
    .lang-panel-wrap {
      position: relative;
    }
    /* Closed state keeps the node in the tree (opacity + pointer-events) so the
       300ms fade has something to animate, exactly as www does. aria-hidden and
       inert keep it off the a11y tree and out of the tab order meanwhile —
       opacity alone would leave a keyboard user tabbing into invisible links. */
    .lang-panel {
      transition: opacity 0.3s ease-in-out;
      opacity: 0;
      pointer-events: none;
    }
    .lang-panel[data-open] {
      opacity: 1;
      pointer-events: auto;
    }
    .lang-list {
      box-sizing: border-box;
      width: 100%;
      background: var(--pm-footer-bg, #fff);
      border-color: var(--pm-footer-rule, #e6e6e6);
      padding: 10px;
      margin-bottom: 0.8rem;
    }
    /* www promotes the list to a floating card only at xl (1280px); below that
       it stays inline and pushes the social row down. Same breakpoint here. */
    @media (min-width: 1280px) {
      .lang-list {
        position: absolute;
        right: 0;
        bottom: 100%;
        width: max-content;
        border: 1px solid var(--pm-footer-rule, #e6e6e6);
        border-radius: 10px;
        padding: 20px;
        box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
        z-index: 20;
      }
    }
    .lang-list-label {
      margin: 0 0 10px 10px;
      font-size: 14px;
      text-transform: uppercase;
      color: var(--pm-footer-accent, #ff6c37);
    }
    .lang-option {
      border-radius: 5px;
      padding: 10px;
      transition: background-color 0.15s ease-in-out;
    }
    .lang-option:hover {
      background: var(--pm-footer-option-hover, #efefef);
    }
    /* Overrides the generic footer anchor rule (grey, 14px, 5px padding): options are
       near-black and semibold, and the row's own padding does the spacing. */
    .lang-option a {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 0;
      font-size: 14px;
      font-weight: 600;
      color: var(--pm-footer-option-fg, #212121);
    }
    .lang-option a:hover {
      color: var(--pm-footer-option-fg, #212121);
    }
    /* The tick is always in the DOM, faded when the locale is not current, so
       the labels stay on a single left edge instead of jumping 18px when the
       selection moves. */
    .lang-check {
      flex: none;
      transition: opacity 0.15s ease-in-out;
    }
    .lang-option a:not([aria-current]) .lang-check {
      opacity: 0.25;
    }
    .lang-option a:not([aria-current]):hover .lang-check {
      opacity: 0.5;
    }
    /* gap-2 (20px), and no margin of its own: on www the columns block owns the
       gap above (mb-3 / lg:mb-0) and the rule owns the gap below (my-2). The
       8px margin v1.0.4 had here collapsed with the rule's margin and made the
       spacing above the rule impossible to reason about. */
    .social {
      display: flex;
      gap: 20px;
      justify-content: flex-end;
    }
    @media (max-width: 1023.98px) {
      .social {
        justify-content: center;
      }
    }
    /* 5px 0 is Footer.module.css's a.link padding verbatim. The zero horizontal
       padding is what turns the 20px gap into www's 36px icon-to-icon pitch;
       v1.0.4's uniform 4px added 8px per icon and shrank the gap to 8px. */
    .social a {
      padding: 5px 0;
      line-height: 0;
    }
    .social img {
      width: 16px;
      height: 16px;
      opacity: 0.7;
      filter: grayscale(100%);
      transition: 0.2s ease-in-out;
    }
    .social img:hover {
      opacity: 1;
      filter: grayscale(0);
    }
    /* 20px, at every width. www's my-2 is 2rem on a 10px root, so the stock-scale
       8px v1.0.4 shipped left the rule visibly crowded against the social icons
       above and the legal links below — the most obvious symptom of the scale
       mix-up documented at the top of this block. */
    hr {
      border: 0;
      border-top: 1px solid var(--pm-footer-rule, #e6e6e6);
      margin: 20px 0;
    }
    .bottom {
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      align-items: start;
      /* lg:gap-x-4 (40px) with lg:gap-y-0 — a single row needs no row gap. */
      column-gap: 40px;
      row-gap: 0;
      font-size: 14px;
    }
    @media (max-width: 1023.98px) {
      .bottom {
        grid-template-columns: 1fr;
        text-align: center;
        /* gap-y-2 (20px), which only bites once the row stacks. The column gap
           goes back to none: www's is lg:gap-x-4, so it does not apply here. No
           visual effect on one column, but leaving 40px in place would silently
           become a real gap if this grid ever gains a second column. */
        column-gap: normal;
        row-gap: 20px;
      }
    }
    /* Bottom-bar action links are the site's blue, not the column grey — and
       unlike the column links they carry no vertical padding (17px tall, not
       34px), which is what keeps the bar 42px rather than 61px. */
    .bottom a {
      color: var(--pm-footer-action, #007bff);
      font-size: 14px;
      padding: 0;
      line-height: normal;
    }
    .bottom a:hover {
      color: #0056b3;
    }
    /* gap-y-1 (10px), the spacing between the consent controls, the privacy
       policy link and the language switcher. */
    .bottom .center {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }
    /* The privacy / languages slots take host markup, and a consent manager
       hands us a bare <button> — which renders as native OS chrome (grey box,
       border, 13px system font) next to the blue 14px links it sits between.
       www styles "Cookie Preferences" as link text, so strip the button chrome
       here rather than making every consumer restyle it. The font: inherit is
       the part native buttons need; they do not inherit type by default. Only
       the slotted element itself is reachable through ::slotted() — anything
       nested inside it stays the host's to style. */
    .bottom ::slotted(button),
    .bottom ::slotted(a) {
      appearance: none;
      -webkit-appearance: none;
      background: none;
      border: 0;
      padding: 0;
      margin: 0;
      font: inherit;
      font-size: 14px;
      text-decoration: none;
      color: var(--pm-footer-action, #007bff);
      cursor: pointer;
    }
    .bottom ::slotted(button:hover),
    .bottom ::slotted(a:hover) {
      color: #0056b3;
    }
    /* Non-interactive slotted content (e.g. a plain-text language label) reads
       as the column grey, not as an action. */
    .bottom ::slotted(span) {
      font-size: 14px;
      color: var(--pm-footer-muted, #707070);
    }
    /* The bare :focus-visible above only matches elements in this shadow tree,
       so slotted controls need their own ring to keep the reset accessible. */
    .bottom ::slotted(:focus-visible) {
      outline: 2px solid var(--pm-focus-ring, #ff6c37);
      outline-offset: 2px;
    }
    .copyright {
      text-align: right;
      color: var(--pm-footer-muted, #707070);
      /* 22.75px — the 1.625 the footer inherits on www, not the UA default. */
      line-height: 1.625;
      margin: 0;
    }
    @media (max-width: 1023.98px) {
      .copyright {
        text-align: center;
      }
    }

    /* ---------------------------------------------------------------------
       DARK THEME (theme="dark") — ported from Footer.jsx's isHomeV2 branch
       (the redesigned homepage renders on a #0e0704 page background; the
       footer gets the same white-on-dark treatment www-next gives
       state-of-api pages). Scoping to '/' and '/home/' is the consumer's job
       (see postman-navbar's theme doc) — this only implements the look.

       Every rule below already reads a --pm-footer-* custom property, so
       redefining them here is enough for the column links, headings,
       copyright, the bottom bar, and the language panel to all pick up the
       dark palette at once. Only the two hardcoded (non-tokenized) hover
       colors and the language-switcher's checkmark opacity need an explicit
       override. */
    :host([theme='dark']) {
      --pm-footer-bg: #0e0704;
      --pm-footer-fg: #fff;
      --pm-footer-heading: #fff;
      --pm-footer-link: #fff;
      --pm-footer-link-hover: #ff6c37;
      --pm-footer-muted: #fff;
      --pm-footer-action: #fff;
      --pm-footer-rule: rgba(255, 224, 196, 0.1);
      --pm-footer-accent: #ff8e64;
      --pm-footer-option-fg: #cdbfad;
      --pm-footer-option-hover: #241811;
    }
    :host([theme='dark']) .bottom a:hover,
    :host([theme='dark']) .bottom ::slotted(button:hover),
    :host([theme='dark']) .bottom ::slotted(a:hover) {
      color: #ff6c37;
    }
    :host([theme='dark']) .social img {
      opacity: 1;
      filter: brightness(0) invert(1);
    }
    /* Light theme's hover drops the grayscale filter to reveal each icon's own
       brand color — but X's icon asset is solid black (x-social-icon-black.svg),
       which removing the filter entirely would leave invisible against the
       #0e0704 dark bg. Dim the already-white dark-theme icon instead, so every
       icon (including X) stays visible and hover still reads as a state change. */
    :host([theme='dark']) .social img:hover {
      opacity: 0.75;
    }
    :host([theme='dark']) .lang-option a:not([aria-current]) .lang-check {
      opacity: 0.8;
    }
    :host([theme='dark']) .lang-option a:not([aria-current]):hover .lang-check {
      opacity: 1;
    }
  `;

  constructor() {
    super();
    this.locale = 'en';
    this.languageSwitcher = false;
    this._jsonLdInjected = false;
    this._langOpen = false;
    this._hasSlottedLanguages = false;
    // Same shape as <postman-navbar>._onDocPointer, but scoped to the panel
    // wrapper rather than the host: clicking another footer link should close
    // the switcher, and on www it does.
    //
    // composedPath() rather than contains(): at document level `e.target` is
    // retargeted to the host element, so `this.contains(target)` is true for
    // every click anywhere in the footer and would never close the panel.
    this._onDocPointer = (e) => {
      if (!this._langOpen) return;
      const wrap = this.renderRoot?.querySelector('.lang-panel-wrap');
      if (wrap && e.composedPath().includes(wrap)) return;
      this._langOpen = false;
    };
  }

  connectedCallback() {
    super.connectedCallback();
    // Read the LIGHT DOM directly rather than waiting for slotchange. The slot
    // only exists once _showLanguages is true, and _showLanguages is partly
    // what we are trying to determine — so a host that slots a switcher without
    // setting the attribute would deadlock: no slot, no slotchange, no render.
    // Light-DOM children are already parsed by now, so this breaks the cycle.
    //
    // This snapshot is the ONLY thing watching the light DOM. Once the block is
    // rendered the slotchange handler tracks content changing inside the slot,
    // but when it is not rendered there is no slot to fire — so a host that
    // injects [slot="languages"] after connect, with no `language-switcher`
    // attribute to render the block in the first place, is not picked up.
    // Deliberate: catching it needs a MutationObserver on every footer on every
    // page, and no consumer builds its switcher that late. Set the attribute,
    // or have the markup present at connect.
    this._hasSlottedLanguages = !!this.querySelector('[slot="languages"]');
    this.addEventListener('focusout', this._onHostFocusOut);
    document.addEventListener('pointerdown', this._onDocPointer);
  }

  disconnectedCallback() {
    this.removeEventListener('focusout', this._onHostFocusOut);
    document.removeEventListener('pointerdown', this._onDocPointer);
    super.disconnectedCallback();
  }

  get ctx() {
    const origin = this.host || (typeof location !== 'undefined' ? location.origin : '');
    const on = this.translationEnabled;
    return { origin, locale: on ? this.locale : 'en', localizesPages: on };
  }

  /** See <postman-navbar>.translationEnabled for why this is consumer-declared. */
  get translationEnabled() {
    return String(this.translate ?? '').toLowerCase() !== 'false';
  }

  // Copy bundle for the active locale (null for en → inline English source).
  get _copyMap() {
    return this.translationEnabled ? getCopy(this.locale) : null;
  }

  // Whether to emit the light-DOM JSON-LD. On unless explicitly suppressed with
  // `emit-jsonld="false"` (string attr) or `.emitJsonld = false` (property).
  get _emitJsonLd() {
    return String(this.emitJsonld ?? '') !== 'false';
  }

  firstUpdated() {
    // Emit JSON-LD into LIGHT DOM (a child of this element), not the shadow root,
    // so it is discoverable as structured data. Guarded to run once, and skipped
    // when a consumer renders the structured data itself (emit-jsonld="false").
    if (this._jsonLdInjected || typeof document === 'undefined' || !this._emitJsonLd) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(buildFooterJsonLd(footerColumns));
    this.appendChild(script);
    this._jsonLdInjected = true;
  }

  _emitClick(title) {
    this.dispatchEvent(
      new CustomEvent('postman-footer:click', {
        detail: { title },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Whether to render the languages block at all: the host slotted a control,
  // or asked for the built-in one. Checked before rendering so an unused block
  // contributes no margin.
  //
  // `translate="false"` withdraws the BUILT-IN control only. That flag says this
  // consumer serves one language, which is exactly the case the built-in
  // switcher must not advertise — but a host that slots its own control has
  // told us something the flag has no say over, so that one still renders.
  get _showLanguages() {
    return this._hasSlottedLanguages || (this.languageSwitcher && this.translationEnabled);
  }

  // Off ctx.locale, not this.locale: under `translate="false"` ctx pins the
  // locale to en, and the trigger must agree with the links beside it rather
  // than announce 日本語 above a column of English.
  get _currentLanguage() {
    const locale = this.ctx.locale || 'en';
    return footerLanguages.find((l) => l.locale === locale) ?? footerLanguages[0];
  }

  _onLanguagesSlotChange(e) {
    this._hasSlottedLanguages = e.target.assignedNodes({ flatten: true }).length > 0;
  }

  _toggleLanguages() {
    this._langOpen = !this._langOpen;
  }

  // Escape closes and returns focus to the trigger, matching the navbar's panel
  // behaviour. Bound on the wrapper so it catches keys from the options too.
  _onLanguagesKeydown(e) {
    if (e.key !== 'Escape' || !this._langOpen) return;
    e.preventDefault();
    this._langOpen = false;
    this.updateComplete.then(() => this.renderRoot.querySelector('.lang-trigger')?.focus());
  }

  // Focus leaving the SWITCHER dismisses the panel — tabbing off the last
  // option closes it behind you instead of leaving it open over the rest of the
  // footer. Scoped to .lang-panel-wrap, the same boundary _onDocPointer uses,
  // so keyboard and mouse dismiss on the same rule.
  //
  // Reads renderRoot.activeElement rather than e.relatedTarget, which cannot be
  // trusted here: this listener is on the HOST, which lives in the document
  // tree, so a relatedTarget inside the shadow root is retargeted to the host
  // itself. Testing it against anything in the shadow tree is then always false
  // and the panel would collapse the moment focus moved from the trigger to an
  // option. ShadowRoot.activeElement is scoped to this tree and needs no
  // retargeting. (happy-dom does NOT retarget, so a relatedTarget version of
  // this passes the suite and still breaks in every real browser — the same
  // class of gap panel-viewport.test.js documents.)
  //
  // Deferred a tick because focusout fires BEFORE the incoming element takes
  // focus; read synchronously, activeElement is still the outgoing one.
  //
  // Keyboard half only. Clicking a non-focusable element fires no focusout at
  // all — that is _onDocPointer's job. Together they cover what www gets from
  // its single document mousedown listener (LanguageSwitcher.jsx).
  _onHostFocusOut() {
    if (!this._langOpen) return;
    setTimeout(() => {
      if (!this._langOpen) return;
      const wrap = this.renderRoot?.querySelector('.lang-panel-wrap');
      const active = this.renderRoot?.activeElement;
      if (wrap && active && wrap.contains(active)) return;
      this._langOpen = false;
    }, 0);
  }

  _renderLanguages() {
    const current = this._currentLanguage;
    const label = resolveCopy('footer.section.languages', 'Languages', this._copyMap);
    return html`
      <div class="languages">
        <h2 id="footer-languages" part="footer-heading">${label}</h2>
        <slot name="languages" @slotchange=${this._onLanguagesSlotChange}></slot>
        ${this._hasSlottedLanguages ? nothing : this._renderLanguageSwitcher(current)}
      </div>
    `;
  }

  _renderLanguageSwitcher(current) {
    // Singular inside the panel, plural on the heading above it — www uses two
    // different global.json strings here (footer.section.languages vs
    // lang.selectLanguage), so reusing one for both would read wrong in EN and
    // pick the wrong translation in JA.
    const panelLabel = resolveCopy('lang.selectLanguage', 'Language', this._copyMap);
    // The trigger is named by aria-labelledby (the "Languages" heading + the
    // current endonym) rather than an aria-label. www hardcodes
    // aria-label="Select language", which stays English in every locale; there
    // is no global.json key for that string, so translating it would mean
    // inventing a key the bundles do not carry and that falls back to English
    // anyway. Pointing at text already on screen gets a localized name for
    // free — "Languages 日本語" in JA, "Languages English" in EN — and keeps the
    // current value in the accessible name, which a bare label loses.
    return html`
      <div class="lang-panel-wrap" @keydown=${this._onLanguagesKeydown}>
        <button
          part="footer-language-trigger"
          class="lang-trigger"
          type="button"
          aria-labelledby="footer-languages lang-current-value"
          aria-expanded=${this._langOpen ? 'true' : 'false'}
          @click=${this._toggleLanguages}
        >
          <span class="lang-current">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path
                d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
              ></path>
            </svg>
            <span id="lang-current-value" lang=${current.locale}>${current.label}</span>
          </span>
          <svg
            class="lang-chevron"
            width="10"
            height="10"
            viewBox="0 0 12 12"
            aria-hidden="true"
            fill="#6b6b6b"
          >
            <path
              d="M10.375,3.219,6,6.719l-4.375-3.5A1,1,0,1,0,.375,4.781l5,4a1,1,0,0,0,1.25,0l5-4a1,1,0,0,0-1.25-1.562Z"
            ></path>
          </svg>
        </button>
        <div
          class="lang-panel"
          ?data-open=${this._langOpen}
          aria-hidden=${this._langOpen ? 'false' : 'true'}
          ?inert=${!this._langOpen}
        >
          <div class="lang-list">
            <p class="lang-list-label">${panelLabel}</p>
            ${footerLanguages.map((lang) => this._renderLanguageOption(lang, current))}
          </div>
        </div>
      </div>
    `;
  }

  // Where an option leads. On the www family that is THIS page in the other
  // locale ('/pricing/' → '/jp/pricing/'), matching www's own switcher — sending
  // a reader on /pricing/ to the JA homepage loses their place for no reason.
  //
  // Off the www family it is the locale's site ROOT instead. A foreign host's
  // pathname describes that host, not www: translating blog.postman.com/2024/
  // /some-post/ into www.postman.com/jp/2024/some-post/ would invent a URL that
  // has never existed. The root is the only honest destination we can name.
  _languageHref(lang) {
    return isWwwFamily(this.ctx.origin)
      ? localeSwitchPath(lang.locale)
      : resolveHref({ type: 'internal', path: lang.path, i18n: false }, this.ctx);
  }

  _renderLanguageOption(lang, current) {
    const isCurrent = lang.locale === current.locale;
    return html`<div class="lang-option">
      <a
        part="footer-language-option"
        href=${this._languageHref(lang)}
        lang=${lang.locale}
        aria-current=${isCurrent ? 'true' : nothing}
        @click=${() => this._emitClick(lang.label)}
      >
        <svg class="lang-check" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <polyline
            points="1.5,6 4.5,9 10.5,3"
            fill="none"
            stroke=${isCurrent ? '#FF6C37' : '#6b6b6b'}
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></polyline>
        </svg>
        ${lang.label}
      </a>
    </div>`;
  }

  _link(item) {
    const target = item.link?.target;
    const label = resolveItemCopy(item, this._copyMap).title;
    return html`<a
      part="footer-link"
      href=${resolveHref(item.link, this.ctx)}
      target=${target ?? nothing}
      rel=${target === '_blank' || item.link?.type === 'external' ? 'noopener noreferrer' : nothing}
      @click=${() => this._emitClick(item.title)}
      >${label}</a
    >`;
  }

  render() {
    const year = this.year ?? new Date().getFullYear();
    return html`
      <footer part="footer" aria-label="Footer">
        <div class="inner">
          <div class="columns">
            <div class="logo">
              <a
                part="footer-link"
                href=${resolveHref(footerBottom.home ?? { type: 'internal', path: '/', i18n: true }, this.ctx)}
                aria-label="Postman"
              >
                <img src=${footerBottom.logo} alt="Postman API Platform" width="100" height="90" />
              </a>
            </div>
            ${footerColumns.map((col, i) => {
              const nav = html`<nav aria-labelledby="col-${col.id}">
                <h2 id="col-${col.id}" part="footer-heading">
                  ${resolveCopy(col.key, col.title, this._copyMap)}
                </h2>
                <ul>
                  ${col.items.map((item) => html`<li>${this._link(item)}</li>`)}
                </ul>
              </nav>`;
              // www hangs the languages block off the LAST column rather than
              // giving it a grid cell, which is what keeps its heading on the
              // same left edge as "Company". Only that column gets wrapped, so
              // a footer without a switcher lays out exactly as it did before.
              const isLast = i === footerColumns.length - 1;
              return isLast && this._showLanguages
                ? html`<div class="col-langs">${nav}${this._renderLanguages()}</div>`
                : nav;
            })}
          </div>

          <div class="social">
            ${footerSocial.map(
              (s) =>
                html`<a
                  part="footer-social"
                  href=${s.url}
                  aria-label=${`Follow Postman on ${s.label}`}
                  rel="noopener noreferrer"
                  @click=${() => this._emitClick(s.label)}
                >
                  <img src=${s.icon} alt="" aria-hidden="true" />
                </a>`
            )}
          </div>

          <hr />

          <div class="bottom">
            <div>${this._link(footerBottom.download)}</div>
            <div class="center">
              <slot name="privacy"></slot>
              ${this._link(footerBottom.privacyPolicy)}
            </div>
            <p class="copyright">© ${year} Postman, Inc.</p>
          </div>
        </div>
      </footer>
    `;
  }
}

// Guarded rather than a bare define(): a page that loads BOTH bundles — the
// hydrate one for a prerendered navbar and the client one for a footer, which is
// exactly what a consumer adopting them separately ends up with — would
// otherwise throw NotSupportedError on the second define and abort that whole
// bundle, taking the element it was loaded for with it.
//
// First definition wins. If the two bundles are different VERSIONS the older may
// win silently, so consumers should still load one; this makes the mistake
// harmless rather than fatal.
if (!customElements.get('postman-footer')) {
  customElements.define('postman-footer', PostmanFooter);
}
